import { CommonModule, DatePipe, isPlatformBrowser, Location } from '@angular/common';
import type { HttpRequest } from '@angular/common/http';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
    APP_ID,
    APP_INITIALIZER,
    ErrorHandler,
    importProvidersFrom,
    inject,
    Injector,
    PLATFORM_ID,
    provideAppInitializer,
    provideCheckNoChangesConfig,
    provideZonelessChangeDetection,
    TransferState,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    provideClientHydration,
    withEventReplay,
    withHttpTransferCacheOptions,
    withI18nSupport,
    withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { UrlSerializer } from '@angular/router';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { CookieService } from 'ngx-cookie-service';
import type { Observable } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';

import { EscapeKeydownDispatcherService, FocusTrapDispatcherService, ModalModule } from 'components/organisms/modal';
import { EAGER_WIDGETS_COUNT, WIDGETS_CONFIG_MAP } from 'components/organisms/widgets-loader/entities/const';
import type { IWidgetsConfigMap } from 'components/organisms/widgets-loader/entities/widget-config';
import { WidgetsService } from 'components/organisms/widgets-loader/widgets.service';
import { WIDGETS_CONFIG } from 'components/widgets/widgets-config';
import { CdnImageUrlPipe } from 'src/pipes/cdn-image-url/cdn-image-url.pipe';
import { StringFormatPipe } from 'src/pipes/string-format/string-format.pipe';
import { l10nDefaultConfig } from 'src/providers/l10n/l10n-default-config';
import { L10nModule } from 'src/providers/l10n/l10n.module';
import { BrandingHeadService } from 'src/services/branding-head/branding-head.service';
import { CacheFlusherInterceptor } from 'src/services/cache-flusher/cache-flusher.interceptor';
import { provideConsentManagementPlatform } from 'src/services/consent-management/consent-providers.factory';
import { EnvironmentService } from 'src/services/environment/environment.service';
import { GeoService } from 'src/services/geo/geo.service';
import { HTTP_ERROR_HANDLER_SERVICE } from 'src/services/http-error-handler/entities/const';
import { HttpErrorHandlerService } from 'src/services/http-error-handler/http-error-handler.service';
import { LocaleService } from 'src/services/locale/locale.service';
import { SimpleDto } from 'src/services/marketo';
import { PageLocalizationService } from 'src/services/page-localization/page-localization.service';
import { WaitForGaCookieService } from 'src/services/personal-data-protection/implementations/wait-for-ga-cookie/wait-for-ga-cookie.service';
import { PersonalDataProtectionModule } from 'src/services/personal-data-protection/personal-data-protection.module';
import { WAIT_FOR_GA_COOKIE } from 'src/services/personal-data-protection/tokens';
import { RegistrationService } from 'src/services/registration/registration.service';
import { LazySentryErrorHandler } from 'src/services/sentry/lazy-sentry-error-handler';
import { scheduleSentryLoad } from 'src/services/sentry/sentry-loader';
import { UrlService } from 'src/services/url/url.service';
import { UrlLocaliseService } from 'src/services/url-localise/url-localise.service';
import { WrikeUrlSerializer } from 'src/services/wrike-url-serializer/wrike-url-serializer.service';
import { IS_EU_VISITOR } from 'src/shared/constants/is-eu-visitor/is-eu-visitor';
import { contactSalesFormDefaultOptionsFactory } from 'src/shared/helpers/default-contact-sales-form-options/default-contact-sales-form-options.factory';
import { trialFormDefaultOptionsFactory } from 'src/shared/helpers/default-trial-form-options/default-trial-form-options.factory';
import { translateModuleConfig } from 'src/shared/helpers/translate-module-config/translate-module-config';
import { WrikeDynamicBaseHrefModule } from 'src/shared/helpers/wrike-dynamic-base-href-routing/wrike-dynamic-base-href.module';
import {
    API_VERSION,
    FULL_API_ENDPOINT,
} from 'src/tokens/business-email-validator/business-email-validator-environments';
import { CDN_ENV, IMAGES_CDN_URL } from 'src/tokens/cdn-environment/cdn-environment';
import {
    DEFAULT_CONTACT_SALES_FORM_OPTIONS,
    DEFAULT_TRIAL_FORM_OPTIONS,
} from 'src/tokens/default-form-params-options/default-form-params-options';
import { ENVIRONMENT_NAME } from 'src/tokens/environment-name/environment-name';
import { ENVIRONMENT_ORIGIN } from 'src/tokens/environment-origin/environment-origin';
import { IS_QAA_PAGE } from 'src/tokens/is-qaa-page/is-qaa-page';
import { MAP_SOURCE_TO_SUBDOMAINS } from 'src/tokens/map-source-to-subdomains/map-source-to-subdomains';
import { PRODUCT_TYPE } from 'src/tokens/product-type/product-type';
import { LOGIN_ENV, LOGIN_PAGE_LINK, REGISTRATION_DOMAIN } from 'src/tokens/registration/registration-environments';

import { AppRoutingModule } from './app-routing.module';
import { APP_VERSION, APP_VERSION_KEY } from './app-version';

export const appConfig: ApplicationConfig = {
    providers: [
        provideClientHydration(
            withIncrementalHydration(),
            withEventReplay(),
            withI18nSupport(),
            withHttpTransferCacheOptions({
                filter: (req: HttpRequest<unknown>) => {
                    // This code disables the default transfer state caching for all HttpClient requests.
                    // By default, Angular caches all requests in the transfer state, which can result in excessive cache size
                    // and unnecessary data being sent to the client. Now only the required data is cached according to our logic.
                    // This is done to prevent conflicts, since we have our own implementation of caching and transfer state usage within rest service.
                    return false;
                },
            })
        ),
        provideZonelessChangeDetection(),
        provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 }),
        ...provideTranslateService({
            loader: translateModuleConfig.loader,
            missingTranslationHandler: translateModuleConfig.missingTranslationHandler,
        }),
        importProvidersFrom(
            CommonModule,
            FormsModule,
            RecaptchaV3Module,
            WrikeDynamicBaseHrefModule.forRoot(),
            L10nModule.forRoot(l10nDefaultConfig),
            PersonalDataProtectionModule,
            ModalModule,
            AppRoutingModule
        ),
        SimpleDto,
        CookieService,
        ...provideConsentManagementPlatform(),
        RegistrationService,
        UrlLocaliseService,
        LocaleService,
        EscapeKeydownDispatcherService,
        FocusTrapDispatcherService,
        CdnImageUrlPipe,
        StringFormatPipe,
        PageLocalizationService,
        UrlService,
        DatePipe,
        {
            provide: UrlSerializer,
            useClass: WrikeUrlSerializer,
        },
        {
            provide: CDN_ENV,
            useFactory: ({ environment }: EnvironmentService) => environment.envName,
            deps: [EnvironmentService],
        },
        {
            provide: IMAGES_CDN_URL,
            useFactory: ({ environment }: EnvironmentService) => environment.imagesCdnUrl,
            deps: [EnvironmentService],
        },
        {
            provide: LOGIN_ENV,
            useFactory: ({ environment }: EnvironmentService) => environment.envName,
            deps: [EnvironmentService],
        },
        {
            provide: LOGIN_PAGE_LINK,
            useFactory: ({ environment }: EnvironmentService) => environment.loginPageLink,
            deps: [EnvironmentService],
        },
        {
            provide: REGISTRATION_DOMAIN,
            useFactory: ({ environment }: EnvironmentService) => environment.registrationDomain,
            deps: [EnvironmentService],
        },
        {
            provide: APP_ID,
            useValue: 'tp_fe',
        },
        {
            provide: API_VERSION,
            useFactory: ({ environment }: EnvironmentService) => environment.apiVersion,
            deps: [EnvironmentService],
        },
        {
            provide: FULL_API_ENDPOINT,
            useFactory: ({ environment }: EnvironmentService) =>
                `${environment.apiOrigin}${environment.apiProject}${environment.apiEndpoint}`,
            deps: [EnvironmentService],
        },
        {
            provide: ENVIRONMENT_ORIGIN,
            useFactory: ({ environment }: EnvironmentService) => environment.origin,
            deps: [EnvironmentService],
        },
        {
            provide: ENVIRONMENT_NAME,
            useFactory: ({ environment }: EnvironmentService) => environment.envName,
            deps: [EnvironmentService],
        },
        {
            provide: MAP_SOURCE_TO_SUBDOMAINS,
            useFactory: ({ environment }: EnvironmentService) => environment.mapSourceToSubdomains,
            deps: [EnvironmentService],
        },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useFactory: ({ environment }: EnvironmentService) => environment.googleSiteKey,
            deps: [EnvironmentService],
        },
        {
            provide: PRODUCT_TYPE,
            useFactory: ({ environment }: EnvironmentService) => environment.productType,
            deps: [EnvironmentService],
        },

        {
            provide: HTTP_ERROR_HANDLER_SERVICE,
            useClass: HttpErrorHandlerService,
        },
        {
            provide: WAIT_FOR_GA_COOKIE,
            useClass: WaitForGaCookieService,
        },
        {
            provide: WIDGETS_CONFIG_MAP,
            useValue: WIDGETS_CONFIG,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheFlusherInterceptor,
            multi: true,
        },
        {
            provide: DEFAULT_CONTACT_SALES_FORM_OPTIONS,
            useFactory: contactSalesFormDefaultOptionsFactory,
            deps: [GeoService, TranslateService, EnvironmentService, PRODUCT_TYPE],
        },
        {
            provide: DEFAULT_TRIAL_FORM_OPTIONS,
            useFactory: trialFormDefaultOptionsFactory,
            deps: [TranslateService, EnvironmentService],
        },
        {
            provide: IS_EU_VISITOR,
            useFactory: (cookieService: CookieService) => cookieService.get('is_eu_visitor') === '1',
            deps: [CookieService],
        },
        {
            provide: IS_QAA_PAGE,
            useFactory: (location: Location) => location.path().includes('/tools/qaa'),
            deps: [Location],
        },
        provideAppInitializer(() => {
            const brandingHeadService = inject(BrandingHeadService);
            brandingHeadService.init();
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: function factory(
                transferState: TransferState,
                widgetsService: WidgetsService,
                injector: Injector
            ): () => Observable<IWidgetsConfigMap> {
                return () => {
                    const widgetsConfig = injector.get(WIDGETS_CONFIG_MAP);
                    const widgets: string[] = transferState.get(widgetsService.widgetsStateKey, []);
                    widgetsService.fetchWidgets(
                        Object.assign(WIDGETS_CONFIG, widgetsConfig),
                        widgets.slice(0, EAGER_WIDGETS_COUNT)
                    );
                    return widgetsService.$widgets.pipe(
                        skipWhile((preloadedWidgetsConfig) => Object.keys(preloadedWidgetsConfig).length === 0),
                        take(1)
                    );
                };
            },
            deps: [TransferState, WidgetsService, Injector],
            multi: true,
        },
        // Browser only: the SDK is pulled in on idle after `load`, or earlier if an error shows up.
        // SSR initializes Sentry eagerly from app.config.server.ts.
        provideAppInitializer(() => {
            const platformId = inject(PLATFORM_ID) as string;

            if (isPlatformBrowser(platformId)) {
                scheduleSentryLoad(inject(EnvironmentService).environment, platformId, inject(APP_VERSION));
            }
        }),
        {
            provide: ErrorHandler,
            useClass: LazySentryErrorHandler,
        },
        {
            provide: APP_VERSION,
            useFactory: (transferState: TransferState) => {
                return transferState.get(APP_VERSION_KEY, '');
            },
            deps: [TransferState],
        },
        provideAnimationsAsync(),
        provideHttpClient(withInterceptorsFromDi()),
    ],
};
